/**
 * Vendor Portal Service Hooks
 * React hooks for accessing vendor portal services (vendor self-service)
 */
import { useCCMS } from '../useCCMS';
/**
 * Hook to access all vendor portal services
 * @returns VendorPortalServices object containing all vendor portal service instances
 */
export function useVendorPortalServices() {
    const { services } = useCCMS();
    if (!services?.vendorPortal) {
        throw new Error('Vendor Portal services not available. Ensure CCMSProvider is configured with vendorPortal services.');
    }
    return services.vendorPortal;
}
/**
 * Hook to access VendorPortalProfileService
 * Used for vendor profile management (view and update own profile)
 */
export function useVendorPortalProfileService() {
    return useVendorPortalServices().profile;
}
/**
 * Hook to access VendorPortalDocumentService
 * Used for vendor document management (CRUD on own documents)
 */
export function useVendorPortalDocumentService() {
    return useVendorPortalServices().documents;
}
/**
 * Hook to access VendorPortalLocationService
 * Used for vendor operational location management
 */
export function useVendorPortalLocationService() {
    return useVendorPortalServices().locations;
}
/**
 * Hook to access VendorPortalPaymentService
 * Used for vendor payment configuration management (CRUD)
 */
export function useVendorPortalPaymentService() {
    return useVendorPortalServices().payments;
}
/**
 * Hook to access VendorPortalContactService
 * Used for vendor contact management (CRUD on own contacts)
 */
export function useVendorPortalContactService() {
    return useVendorPortalServices().contacts;
}
/**
 * Hook to access VendorPortalActivityService
 * Used for viewing vendor activity logs and status history (read-only)
 */
export function useVendorPortalActivityService() {
    return useVendorPortalServices().activity;
}
/**
 * Hook to access VendorPortalProductService
 * Used for vendor product management (CRUD on own products)
 */
export function useVendorPortalProductService() {
    return useVendorPortalServices().products;
}
/**
 * Hook to access VendorPortalProductTypeService
 * Used for reading product type reference data (read-only)
 */
export function useVendorPortalProductTypeService() {
    return useVendorPortalServices().productTypes;
}
/**
 * Hook to access VendorPortalProductVariantService
 * Used for vendor product variant management
 */
export function useVendorPortalProductVariantService() {
    return useVendorPortalServices().productVariants;
}
/**
 * Hook to access VendorPortalProductMediaService
 * Used for vendor product media management
 */
export function useVendorPortalProductMediaService() {
    return useVendorPortalServices().productMedia;
}
/**
 * Hook to access VendorPortalProductAnalyticsService
 * Used for viewing vendor product analytics (read-only)
 */
export function useVendorPortalProductAnalyticsService() {
    return useVendorPortalServices().productAnalytics;
}
/**
 * Hook to access VendorPortalRatingService
 * Used for viewing vendor ratings (read-only)
 */
export function useVendorPortalRatingService() {
    return useVendorPortalServices().ratings;
}
/**
 * Hook to access VendorPortalResponseService
 * Used for managing vendor responses to ratings (CRUD)
 */
export function useVendorPortalResponseService() {
    return useVendorPortalServices().responses;
}
/**
 * Hook to access VendorPortalFlagService
 * Used for viewing vendor flags (read-only)
 */
export function useVendorPortalFlagService() {
    return useVendorPortalServices().flags;
}
/**
 * Hook to access VendorPortalEventRegistrationService
 * Used for managing event registrations (view, confirm, decline)
 */
export function useVendorPortalEventRegistrationService() {
    return useVendorPortalServices().eventRegistrations;
}
/**
 * Hook to access VendorPortalProductEventLimitService
 * Used for managing product event limits (quantity limits per event)
 */
export function useVendorPortalProductEventLimitService() {
    return useVendorPortalServices().productEventLimits;
}
/**
 * Hook to access VendorPortalTermService
 * Used for vendor term management (view, create, update own terms)
 */
export function useVendorPortalTermService() {
    return useVendorPortalServices().terms;
}
/**
 * Hook to access VendorPortalDonationService
 * Used for viewing vendor donations (read-only)
 */
export function useVendorPortalDonationService() {
    return useVendorPortalServices().donations;
}
/**
 * Hook to access VendorPortalSocialMediaService
 * Used for managing vendor social media links (CRUD)
 */
export function useVendorPortalSocialMediaService() {
    return useVendorPortalServices().socialMedia;
}
/**
 * Hook to access VendorPortalProductAddOnService
 * Used for managing vendor product add-ons (CRUD)
 */
export function useVendorPortalProductAddOnService() {
    return useVendorPortalServices().productAddOns;
}
/**
 * Hook to access VendorPortalProductAddOnCategoryService
 * Used for managing vendor product add-on categories (CRUD)
 */
export function useVendorPortalProductAddOnCategoryService() {
    return useVendorPortalServices().productAddOnCategories;
}
/**
 * Hook to access VendorPortalProductVariantCategoryService
 * Used for managing vendor product variant categories (CRUD)
 */
export function useVendorPortalProductVariantCategoryService() {
    return useVendorPortalServices().productVariantCategories;
}
//# sourceMappingURL=useVendorPortalServices.js.map